import { useCallback, useMemo } from "react";
import type { WorkspaceSchema } from "@common/schemas/workspace.schema";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import type { ClientSchema, MemberSchema } from "@common/schemas/client.schema";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import type { ChartOfAccountsTemplate } from "@common/types/chartOfAccountsTemplate";
import { useLedgersByEntityIds } from "@api/ledger.api";
import { useCoaLastUpdatedByEntityIds } from "@api/account.api";
import { useAllClientsForOrgQuery } from "@api/workspace.api";
import { isEntityApplicable } from "@common/helpers/chartOfAccountsTemplateApplicability";
import { useScope } from "@state/scopeContext.lib";
import type { ApplyCoaTemplateEntitySelectionRow } from "./applyCoaTemplateEntitySelection.types";

type WorkspaceEntity = WorkspaceSchema["entities"][number];

interface Params {
    workspaces: WorkspaceSchema[] | undefined;
    template: ChartOfAccountsTemplate | undefined;
    /**
     * When provided, the tree includes only entities whose id is in this set
     * (used by the read-only applied-entities drawer). When omitted, falls back
     * to the apply-wizard applicability predicate.
     */
    allowedEntityIds?: ReadonlySet<number>;
    /**
     * ISO date the template was applied to each entity, keyed by entity id.
     * Surfaced in the "Applied" column by the applied-entities drawer; omitted
     * by the apply wizard (rows then carry `appliedAt: null`).
     */
    appliedAtByEntityId?: ReadonlyMap<number, string>;
}

interface Result {
    tableData: ApplyCoaTemplateEntitySelectionRow[];
    applicableEntityIds: Set<string>;
    isLoading: boolean;
    isError: boolean;
}

// Client-level columns (Team / Services) attached to every row. Entity rows
// inherit team/services from their client.
interface ClientEnrichment {
    team: MemberSchema[];
    services: ClientSchema["services"];
}

function buildClientEnrichment(
    client: ClientSchema,
    organizationId: string | null,
): ClientEnrichment {
    const membersById = new Map<number, MemberSchema>();
    for (const entity of client.entities) {
        for (const member of entity.members ?? []) {
            membersById.set(member.userId, member);
        }
    }
    const team = [...membersById.values()].filter(
        (member) => member.organizationId === organizationId,
    );

    return {
        team,
        services: client.services,
    };
}

/**
 * Builds the workspace → entity tree for the select-entities step, keeping only
 * entities applicable to the template and hiding workspaces with none.
 *
 * Applicability comes from the shared `isEntityApplicable` helper; industry and
 * basis are optional metadata and do not gate apply (PRO-9997). Ledger bases
 * are still loaded for the Basis column / filter. The Clients query enriches
 * each row with Team / Services, and the CoA-last-updated query supplies the
 * "Last updated" column per entity.
 */
export function useApplyCoaTemplateEntityTree({
    workspaces,
    template,
    allowedEntityIds,
    appliedAtByEntityId,
}: Params): Result {
    const { organizationId } = useScope();

    const includeEntity = useCallback(
        (entity: WorkspaceEntity, workspace: WorkspaceSchema): boolean => {
            if (allowedEntityIds) {
                return allowedEntityIds.has(entity.id);
            }
            if (!template) {
                return false;
            }
            return isEntityApplicable({
                entityLegalType: entity.legalType,
                isBusiness: !entity.isPersonal,
                isMockEntity: entity.isMockEntity,
                hasCustomChartOfAccounts: entity.hasCustomChartOfAccounts,
                isGlFirst: workspace.glFirstEnabled === true,
                template,
            });
        },
        [allowedEntityIds, template],
    );

    const {
        data: clientsResponse,
        isLoading: isLoadingClients,
        isError: isClientsError,
    } = useAllClientsForOrgQuery(organizationId ?? "");

    const enrichmentByWorkspaceId = useMemo(() => {
        const map = new Map<string, ClientEnrichment>();
        for (const client of clientsResponse?.clients ?? []) {
            map.set(
                client.id,
                buildClientEnrichment(client, organizationId ?? null),
            );
        }
        return map;
    }, [clientsResponse, organizationId]);

    const candidateEntityIds = useMemo(() => {
        if (!workspaces?.length || !template) {
            return [];
        }
        const ids: number[] = [];
        for (const workspace of workspaces) {
            if (
                !allowedEntityIds &&
                workspace.access.workspaceRole === UserWorkspaceRole.VIEW_ONLY
            ) {
                continue;
            }
            for (const entity of workspace.entities ?? []) {
                if (includeEntity(entity, workspace)) {
                    ids.push(entity.id);
                }
            }
        }
        return ids;
    }, [workspaces, template, includeEntity, allowedEntityIds]);

    const {
        data: ledgersByEntityId,
        isLoading: isLoadingBases,
        isError: isLedgersError,
    } = useLedgersByEntityIds(candidateEntityIds);

    const {
        data: coaLastUpdatedByEntityId,
        isLoading: isLoadingCoaUpdated,
        isError: isCoaUpdatedError,
    } = useCoaLastUpdatedByEntityIds(candidateEntityIds);

    const basesByEntityId = useMemo(() => {
        const map = new Map<number, AccountingLedgerBasis[]>();
        if (!ledgersByEntityId) {
            return map;
        }
        for (const [entityIdKey, ledgers] of Object.entries(
            ledgersByEntityId,
        )) {
            const entityId = Number(entityIdKey);
            const distinctBases = [
                ...new Set(ledgers.map((ledger) => ledger.basis)),
            ];
            map.set(entityId, distinctBases);
        }
        return map;
    }, [ledgersByEntityId]);

    const tableData = useMemo<ApplyCoaTemplateEntitySelectionRow[]>(() => {
        if (!workspaces?.length || !template) {
            return [];
        }
        const rows: ApplyCoaTemplateEntitySelectionRow[] = [];
        for (const workspace of workspaces) {
            // View-only workspaces never appear as apply targets: their entities
            // would fail the preview 404 (backend refuses the write). The 403 is
            // defense in depth for direct API calls (PRO-11491 / A3). The
            // read-only applied-entities drawer (allowedEntityIds) is exempt so
            // it can still show history for a now-view-only workspace.
            if (
                !allowedEntityIds &&
                workspace.access.workspaceRole === UserWorkspaceRole.VIEW_ONLY
            ) {
                continue;
            }
            const enrichment = enrichmentByWorkspaceId.get(workspace.id);
            const children: ApplyCoaTemplateEntitySelectionRow[] = [];
            for (const entity of workspace.entities ?? []) {
                if (!includeEntity(entity, workspace)) {
                    continue;
                }
                const entityBases = basesByEntityId.get(entity.id) ?? [];
                children.push({
                    id: String(entity.id),
                    isClientRow: false,
                    workspaceId: workspace.id,
                    name: entity.name ?? String(entity.id),
                    iconColor: entity.iconColor ?? "",
                    logo: entity.logo ?? null,
                    entityId: entity.id,
                    industries: entity.industries,
                    bases: entityBases,
                    team: enrichment?.team ?? [],
                    services: enrichment?.services ?? null,
                    coaLastUpdatedAt:
                        coaLastUpdatedByEntityId?.[entity.id] ?? null,
                    appliedAt: appliedAtByEntityId?.get(entity.id) ?? null,
                });
            }
            if (children.length === 0) {
                continue;
            }
            rows.push({
                id: `ws-${workspace.id}`,
                isClientRow: true,
                workspaceId: workspace.id,
                name: workspace.name,
                iconColor: workspace.iconColor ?? "",
                logo: null,
                entityId: null,
                industries: [],
                bases: [],
                team: enrichment?.team ?? [],
                services: enrichment?.services ?? null,
                coaLastUpdatedAt: null,
                appliedAt: null,
                children,
            });
        }
        return rows;
    }, [
        workspaces,
        template,
        includeEntity,
        allowedEntityIds,
        basesByEntityId,
        enrichmentByWorkspaceId,
        coaLastUpdatedByEntityId,
        appliedAtByEntityId,
    ]);

    const applicableEntityIds = useMemo(
        () =>
            new Set(
                tableData.flatMap(
                    (workspaceRow) =>
                        workspaceRow.children?.map((child) => child.id) ?? [],
                ),
            ),
        [tableData],
    );

    return {
        tableData,
        applicableEntityIds,
        // Gate the step on both the bases and the client-enrichment queries:
        // rendering rows before enrichment loads would briefly drop every row
        // when a Team/Services filter is active (empty arrays match nothing).
        isLoading:
            (candidateEntityIds.length > 0 && isLoadingBases) ||
            (candidateEntityIds.length > 0 && isLoadingCoaUpdated) ||
            isLoadingClients,
        // Surfaced so the step can show an error state instead of silently
        // rendering "no applicable clients": a failed ledgers query leaves every
        // entity with empty bases (filtering them all out), and a failed clients
        // query blanks the enrichment columns (breaking Team/Services filters).
        isError:
            (candidateEntityIds.length > 0 && isLedgersError) ||
            (candidateEntityIds.length > 0 && isCoaUpdatedError) ||
            isClientsError,
    };
}
